<script>
	import QuantumBox from "./QuantumBox.svelte";

	const num_particles = 100;

	let r1 = undefined;
	let r1i = 0;
	let r2 = undefined;
	let r2i = 0;

	let r1particles = [];
	let r2particles = [];

	for (let i = 0; i < num_particles; i++) {
		r1particles.push(undefined);
		r2particles.push(undefined);
	}

	function measure1(event) {
		let dir = event.detail.direction;
		r1i = event.detail.particle_idx;
		if (!r1particles[r1i]) {
			// entangled
			let res = Math.random() < 0.5;
			r1particles[r1i] = {
				result: res ? "1" : "0",
				direction: res ? dir : dir + Math.PI,
			};
			r2particles[r1i] = {
				result: res ? "0" : "1",
				direction: res ? dir + Math.PI : dir,
			};
		} else {
			// not entangled
			let theta = r1particles[r1i].direction - dir;
			let res = Math.random() < Math.cos(theta / 2) * Math.cos(theta / 2);
			r1particles[r1i] = {
				result: res ? "1" : "0",
				direction: res ? dir : dir + Math.PI,
			};
		}
		r1 = r1particles[r1i].result;
	}

	function measure2(event) {
		let dir = event.detail.direction;
		r2i = event.detail.particle_idx;
		if (!r2particles[r2i]) {
			// entangled
			let res = Math.random() < 0.5;
			r2particles[r2i] = {
				result: res ? "1" : "0",
				direction: res ? dir : dir + Math.PI,
			};
			r1particles[r2i] = {
				result: res ? "0" : "1",
				direction: res ? dir + Math.PI : dir,
			};
		} else {
			// not entangled
			let theta = r2particles[r2i].direction - dir;
			let res = Math.random() < Math.cos(theta / 2) * Math.cos(theta / 2);
			r2particles[r2i] = {
				result: res ? "1" : "0",
				direction: res ? dir : dir + Math.PI,
			};
		}
		r2 = r2particles[r2i].result;
	}
</script>

<div>
	<QuantumBox
		on:measure={measure1}
		result={r1}
		result_for_idx={r1i}
		box_id="0"
		{num_particles}
	/>
	<QuantumBox
		on:measure={measure2}
		result={r2}
		result_for_idx={r2i}
		box_id="1"
		{num_particles}
	/>
</div>

<h1>Quantum Entanglement</h1>
<p>This is an emulation of the quantum boxes seen in the video <a href="https://www.youtube.com/watch?v=v7jctqKsUMA">Gamification of Bell's Theorem</a> by uniprod.</p>
<p>
	These quantum boxes have {num_particles} pairs of entangled particles.
	You can select a direction with which to measure a given particle's spin.
	It will either be spin up (1) or spin down (0) in the direction measured.
	Initially each particle is entangled with the corresponding particle in the
	other box and the spin is undefined. Once you make a measurement, the particle
	in the other box will have opposite spin (in the given direction) and the
	entanglement will be broken.
</p>
<p>
	To measure using a new pair of entangled particles, either click next particle
	in each box, or reload the page to re-entangle all the particle pairs.
</p>
<p>Watch the video for a more thorough explaination.</p>
<h2>How does it work?</h2>
<p>
	When you visit this site, your browser connects to a real quantum computer and
	temporarily allocates {num_particles} <a href="https://en.wikipedia.org/wiki/Qubit#Qubit_storage">"memory" qubits</a> of medium-term storage to your session.
	Just kidding, it emulates the behaviour using <a href="https://en.wikipedia.org/wiki/Qubit#Bloch_sphere_representation">probability cos^2(theta/2)</a>.
</p>

<style>
	div {
		display: flex;
		gap: 10px;
		margin: 5px;
	}
</style>
